import { MAX_PRODUCT_QTY_DISPLAY } from 'constants/data';
import { formatNumber } from 'utils/FormatNumber';

/**
 * Purchase limits of a SKU (BV-2070).
 *
 * Both limits are optional and come from the CMS "Quantity limitation setting" menu:
 * - limit per day   : sku-limit.quantity (only when the config is active)
 * - limit per order : sku-main.maxQuantityPerOrder
 *
 * A missing limit arrives as null/undefined/0. MAX_PRODUCT_QTY_DISPLAY (100,000) is the legacy
 * "unlimited" sentinel the UI already relies on, so it is treated as "not configured" as well.
 */

export const hasLimitPerDay = (limitPerDay) => Number(limitPerDay) > 0;

export const hasLimitPerOrder = (limitPerOrder) =>
  Number(limitPerOrder) > 0 && Number(limitPerOrder) < MAX_PRODUCT_QTY_DISPLAY;

/**
 * Max quantity per order usable by the UI: the configured value, or the "unlimited" sentinel
 * when nothing is configured (the quantity input clamps to this value).
 */
export const normalizeMaxQuantityPerOrder = (maxQuantityPerOrder) =>
  Number(maxQuantityPerOrder) > 0 ? Number(maxQuantityPerOrder) : MAX_PRODUCT_QTY_DISPLAY;

const fillTemplate = (template, quantity, unit) =>
  template?.replace('{quantity}', formatNumber(quantity)).replace('{unit}', unit) || '';

/**
 * Message shown under a product ("Buy maximum X unit/day and maximum Y unit/order").
 *
 * - nothing configured            -> ''
 * - only per day                  -> per-day message
 * - only per order                -> per-order message
 * - both, per day <= per order    -> per-day message (the per-order limit can never be reached)
 * - both, per day >  per order    -> per-day message + per-order message
 *
 * @param {object} params
 * @param {number|null} params.limitPerDay
 * @param {number|null} params.limitPerOrder
 * @param {string} params.unit
 * @param {{perDay: string, perOrder: string, andPerOrder: string}} params.templates
 *   templates with {quantity} and {unit} placeholders
 */
export const buildLimitOrderMessage = ({ limitPerDay, limitPerOrder, unit, templates = {} }) => {
  const perDayConfigured = hasLimitPerDay(limitPerDay);
  const perOrderConfigured = hasLimitPerOrder(limitPerOrder);

  if (!perDayConfigured && !perOrderConfigured) return '';
  if (!perOrderConfigured) return fillTemplate(templates.perDay, limitPerDay, unit);
  if (!perDayConfigured) return fillTemplate(templates.perOrder, limitPerOrder, unit);
  if (Number(limitPerDay) <= Number(limitPerOrder)) {
    return fillTemplate(templates.perDay, limitPerDay, unit);
  }
  return `${fillTemplate(templates.perDay, limitPerDay, unit)} ${fillTemplate(
    templates.andPerOrder,
    limitPerOrder,
    unit,
  )}`;
};
